import { useEffect, useState } from "react";
import { useAuthStore, api } from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Loader2, Building2, User, BookUser, Lock, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

export default function UnitSettingsPage() {
  const { user: authUser, isAdmin } = useAuthStore();
  const [unit, setUnit] = useState(null);
  const [director, setDirector] = useState("");
  const [accountant, setAccountant] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Profile
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  // Password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const meRes = await api.get("/auth/me");
      setProfileName(meRes.data.name || "");
      setProfileEmail(meRes.data.email || "");
      if (!isAdmin()) {
        const unitRes = await api.get("/my-unit");
        setUnit(unitRes.data);
        setDirector(unitRes.data.director || "");
        setAccountant(unitRes.data.accountant || "");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("loadFailed"));
    } finally { setLoading(false); }
  };

  const saveUnit = async () => {
    setSaving(true);
    try {
      await api.put("/my-unit/settings", { director, accountant });
      toast.success(t("unitSettingsSaved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setSaving(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", { name: profileName, email: profileEmail });
      toast.success(t("profileSaved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) { toast.error(t("passwordMismatch")); return; }
    if (newPw.length < 4) { toast.error(t("passwordTooShort")); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success(t("passwordChanged"));
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setSavingPw(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div data-testid="unit-settings-page" className="max-w-xl mx-auto py-8 px-4 space-y-8">
      {/* Profile Section */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#14532d]/10 flex items-center justify-center">
            <User className="w-5 h-5 text-[#14532d]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#14532d]">{t("myProfile")}</h2>
            <p className="text-xs text-slate-500">{authUser?.role === "CENTRAL_ADMIN" ? t("centralAdmin") : authUser?.unit_name}</p>
          </div>
        </div>
        <div className="space-y-4 bg-white rounded-lg border border-slate-200 p-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
              <User className="w-4 h-4" /> {t("fullName")}
            </label>
            <Input data-testid="profile-name" value={profileName} onChange={e => setProfileName(e.target.value)} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
              <Mail className="w-4 h-4" /> {t("email")}
            </label>
            <Input data-testid="profile-email" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} />
          </div>
          <Button data-testid="save-profile" onClick={saveProfile} disabled={savingProfile}
            className="bg-[#14532d] hover:bg-[#166534] text-white">
            {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t("save")}
          </Button>
        </div>
      </div>

      {/* Password Section */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-700" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">{t("changePassword")}</h2>
        </div>
        <div className="space-y-4 bg-white rounded-lg border border-slate-200 p-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
              <Lock className="w-4 h-4" /> {t("currentPassword")}
            </label>
            <Input data-testid="current-password" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
              <Lock className="w-4 h-4" /> {t("newPassword")}
            </label>
            <Input data-testid="new-password" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
              <Lock className="w-4 h-4" /> {t("confirmPassword")}
            </label>
            <Input data-testid="confirm-password" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
          </div>
          <Button data-testid="change-password-btn" onClick={changePassword} disabled={savingPw || !currentPw || !newPw}
            className="bg-amber-600 hover:bg-amber-700 text-white">
            {savingPw ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            {t("changePassword")}
          </Button>
        </div>
      </div>

      {/* Unit Settings (only for UNIT_USER) */}
      {!isAdmin() && unit && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#14532d]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#14532d]">{t("unitSettings")}</h2>
              <p className="text-xs text-slate-500">{unit.name} ({unit.code})</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-4">{t("unitSettingsDesc")}</p>
          <div className="space-y-4 bg-white rounded-lg border border-slate-200 p-5">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <User className="w-4 h-4" /> {t("directorName")}
              </label>
              <Input data-testid="director-input" value={director} onChange={e => setDirector(e.target.value)} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <BookUser className="w-4 h-4" /> {t("accountantName")}
              </label>
              <Input data-testid="accountant-input" value={accountant} onChange={e => setAccountant(e.target.value)} />
            </div>
            <Button data-testid="save-unit-settings" onClick={saveUnit} disabled={saving}
              className="bg-[#14532d] hover:bg-[#166534] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t("save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
